import { useCallback, useState } from 'react';
import { ITeam } from '@skystone/shared';
import { teamApi } from '@/services/team-api';
import { formatDateTime } from '@/lib/date-utils';
import { getErrorMessage } from '@/lib/utils';
import { DataTable, ColumnDef } from '@/components/ui/DataTable';
import {
  Badge,
  Button,
  ErrorBanner,
  PageHeader,
  PageLoading,
  Tooltip,
} from '@/components/ui';
import { useNavigate } from 'react-router-dom';
import { useApi } from '@/hooks/useApi';
import {
  renderOptionalStringCell,
  renderFabricSyncedCell,
} from '@/lib/table-cell-renderers';
import { PageContainer } from '@/components/layout';
import { CreateTeamDialog } from '@/components/admin/CreateTeamDialog';
import { EditTeamDialog } from '@/components/admin/EditTeamDialog';
import { DeleteTeamConfirm } from '@/components/admin/DeleteTeamConfirm';

/**
 * PRODOPSAI-540: admin-side manual team CRUD. Manual rows (`fabric_synced=false`)
 * are fully editable; Fabric-synced rows keep their fields read-only (the edit
 * button is replaced by a small "Fabric" badge with tooltip).
 *
 * PRODOPSAI-617: archiving is allowed for ALL teams, Fabric-synced included —
 * Fabric carries no team status, so presence in Fabric can't mean "active".
 * A manual archive is sticky: the Fabric sync will not re-activate the team.
 * Archived rows expose a Re-activate action instead of Archive.
 */

interface RowActionHandlers {
  navigate: (path: string) => void;
  onEdit: (team: ITeam) => void;
  onDelete: (team: ITeam) => void;
  onReactivate: (team: ITeam) => void;
}

function renderRowActions(team: ITeam, handlers: RowActionHandlers) {
  // Rabizard #3: View is available on every row regardless of fabric_synced.
  const viewButton = (
    <Button
      variant="outline"
      size="sm"
      onClick={(e) => {
        e.stopPropagation();
        handlers.navigate(`/admin/teams/${team.id}`);
      }}
      data-testid={`team-row-view-${team.id}`}
    >
      View
    </Button>
  );

  // PRODOPSAI-617: Archive (non-archived rows) / Re-activate (archived rows)
  // is available for every team. Only field editing stays Fabric-gated.
  const archiveOrReactivateButton = team.archived === true ? (
    <Button
      variant="outline"
      size="sm"
      onClick={(e) => {
        e.stopPropagation();
        handlers.onReactivate(team);
      }}
      data-testid={`team-row-reactivate-${team.id}`}
    >
      Re-activate
    </Button>
  ) : (
    <Button
      variant="ghost"
      size="sm"
      onClick={(e) => {
        e.stopPropagation();
        handlers.onDelete(team);
      }}
      data-testid={`team-row-delete-${team.id}`}
    >
      Archive
    </Button>
  );

  if (team.fabricSynced === true) {
    return (
      <div className="flex gap-2 justify-end items-center" data-testid={`team-row-fabric-${team.id}`}>
        {viewButton}
        {archiveOrReactivateButton}
        <Tooltip content="Sourced from Fabric — fields are edited in Fabric" side="left" variant="light">
          <Badge variant="secondary">Fabric</Badge>
        </Tooltip>
      </div>
    );
  }
  return (
    <div className="flex gap-2 justify-end">
      {viewButton}
      <Button
        variant="outline"
        size="sm"
        onClick={(e) => {
          e.stopPropagation();
          handlers.onEdit(team);
        }}
        data-testid={`team-row-edit-${team.id}`}
      >
        Edit
      </Button>
      {archiveOrReactivateButton}
    </div>
  );
}

/**
 * PRODOPSAI-617: status cell with archive provenance. 'manual' = an admin
 * archived the team (sticky against Fabric sync); 'sync' = the Fabric sync
 * orphan-archived it (auto-revives if the team reappears in Fabric).
 */
function renderTeamStatusCell(_value: unknown, team: ITeam) {
  if (team.archived !== true) {
    return <Badge variant="success">Active</Badge>;
  }
  const sourceLabel = team.archiveSource === 'manual' || team.archiveSource === 'sync'
    ? ` (${team.archiveSource})`
    : '';
  return <Badge variant="warning">{`Archived${sourceLabel}`}</Badge>;
}

function createTeamColumns(handlers: RowActionHandlers): ColumnDef<ITeam>[] {
  return [
    {
      id: 'name',
      header: 'Name',
      accessor: 'name',
      sortable: true,
      filter: { type: 'text', placeholder: 'Filter name...' },
    },
    {
      id: 'productName',
      header: 'Product',
      accessor: 'productName',
      sortable: true,
      cell: renderOptionalStringCell,
    },
    {
      id: 'regionName',
      header: 'Region',
      accessor: 'regionName',
      sortable: true,
      cell: renderOptionalStringCell,
    },
    {
      id: 'fabricSynced',
      header: 'Fabric',
      accessor: 'fabricSynced',
      cell: renderFabricSyncedCell,
    },
    {
      id: 'archived',
      header: 'Status',
      accessor: 'archived',
      cell: (value, row) => renderTeamStatusCell(value, row),
    },
    {
      id: 'createdAt',
      header: 'Created',
      accessor: 'createdAt',
      sortable: true,
      cell: (value) => formatDateTime(value as string),
    },
    {
      id: 'actions',
      header: 'Actions',
      accessor: 'id',
      cell: (_value, row) => renderRowActions(row, handlers),
    },
  ];
}

export function AdminTeamsPage() {
  const navigate = useNavigate();
  const [showArchived, setShowArchived] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<ITeam | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ITeam | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const { data: teams, loading, error } = useApi(
    () => teamApi.getAll({ includeArchived: showArchived }),
    [showArchived, refreshKey],
  );

  const refresh = useCallback(() => setRefreshKey((n) => n + 1), []);

  const handleCreated = useCallback(
    (created: ITeam) => {
      setStatusMessage(`Created team "${created.name}"`);
      refresh();
    },
    [refresh],
  );

  const handleUpdated = useCallback(
    (updated: ITeam) => {
      setStatusMessage(`Updated team "${updated.name}"`);
      refresh();
    },
    [refresh],
  );

  const handleArchived = useCallback(
    (archived: ITeam) => {
      setStatusMessage(`Archived team "${archived.name}". Memberships preserved.`);
      refresh();
    },
    [refresh],
  );

  const handleReactivate = useCallback(
    async (team: ITeam) => {
      setStatusMessage(null);
      setActionError(null);
      try {
        const reactivated = await teamApi.reactivate(team.id);
        setStatusMessage(`Re-activated team "${reactivated.name}".`);
        refresh();
      } catch (err: unknown) {
        setActionError(getErrorMessage(err, 'Failed to re-activate team'));
      }
    },
    [refresh],
  );

  const columns = createTeamColumns({
    navigate,
    onEdit: (team) => {
      setStatusMessage(null);
      setEditTarget(team);
    },
    onDelete: (team) => {
      setStatusMessage(null);
      setDeleteTarget(team);
    },
    onReactivate: (team) => {
      void handleReactivate(team);
    },
  });

  if (loading) return <PageLoading />;

  return (
    <PageContainer variant="wide" className="space-y-6">
      <PageHeader
        title="Teams"
        subtitle="View and manage teams. Fabric-synced fields are read-only; any team can be archived."
        action={
          <Button
            onClick={() => {
              setStatusMessage(null);
              setCreateOpen(true);
            }}
            data-testid="admin-teams-add-button"
          >
            + Add team
          </Button>
        }
      />

      <ErrorBanner message={error} />
      <ErrorBanner message={actionError} />

      {statusMessage !== null && (
        <output
          className="block rounded-md border border-success-300 bg-success-50 px-4 py-2 text-sm text-success-800"
          data-testid="admin-teams-status"
        >
          {statusMessage}
        </output>
      )}

      <div className="flex items-center gap-2">
        <label htmlFor="show-archived" className="text-sm text-muted-foreground cursor-pointer select-none">
          Show archived
        </label>
        <input
          id="show-archived"
          type="checkbox"
          checked={showArchived}
          onChange={(e) => setShowArchived(e.target.checked)}
          className="h-4 w-4 rounded border-input accent-primary cursor-pointer"
        />
      </div>

      <DataTable
        data={teams ?? []}
        columns={columns}
        keyExtractor={(team) => team.id}
        enablePagination
        enableFilters
        emptyMessage="No teams found."
      />

      <CreateTeamDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSaved={handleCreated}
      />

      <EditTeamDialog
        open={editTarget !== null}
        team={editTarget}
        onClose={() => setEditTarget(null)}
        onSaved={handleUpdated}
      />

      <DeleteTeamConfirm
        open={deleteTarget !== null}
        team={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirmed={handleArchived}
      />
    </PageContainer>
  );
}
